export interface NoticeItem {
  id: string;
  tag: string;
  title: string;
  date: string;
  summary: string;
  body: string[];
}

export const NOTICE_ITEMS: NoticeItem[] = [
  {
    id: 'global-pop-release',
    tag: 'NEW SONGS',
    title: 'Four new Global Pop songs are live.',
    date: 'Aug 3, 2026',
    summary: 'Explore fresh tracks in Drum and try each score level.',
    body: ['Four new Global Pop tracks have been added to DRUM Hi.', 'Open Drum, choose Pop, and select a song to hear its preview before playing. Easy, Normal, and Hard use their registered score formats.'],
  },
  {
    id: 'tablet-preview-update',
    tag: 'SERVICE',
    title: 'DRUM Hi tablet preview update',
    date: 'Jul 28, 2026',
    summary: 'Song previews now start automatically when a track is selected.',
    body: ['Song preview playback now starts automatically when you select a track in Drum.', 'You can change automatic preview playback from My > App settings at any time.'],
  },
  {
    id: 'coaching-feedback',
    tag: 'LEARNING',
    title: 'New rhythm coaching feedback',
    date: 'Jul 20, 2026',
    summary: 'Stage results now include timing, stability and pattern recall feedback.',
    body: ['The result screen now combines game scores with learning feedback.', 'After each performance, review beat stability, timing control, pattern recall, accuracy, and maximum combo to plan the next practice session.'],
  },
];

export interface DifficultyOption {
  id: 'Easy' | 'Normal' | 'Hard';
  format: string;
  color: string;
  description: string;
}

export const DIFFICULTIES: DifficultyOption[] = [
  { id: 'Easy', format: 'Color Drum', color: '#36c46b', description: 'Colored circles, rests and measure bars' },
  { id: 'Normal', format: 'Color Staff', color: '#f5b800', description: 'Full staff notation with instrument colors' },
  { id: 'Hard', format: 'Classic Staff', color: '#ef3f4f', description: 'Standard monochrome drum notation' },
];

export interface PassPlan {
  id: 'drummer' | 'cognitive' | 'plus';
  gradeKo: string;
  gradeEn: string;
  titleKo: string;
  titleEn: string;
  priceKo: string;
  priceEn: string;
  rightsKo: string[];
  rightsEn: string[];
  iconName: 'MusicNotes' | 'Brain' | 'Crown';
}

export const PASS_PLANS: PassPlan[] = [
  {
    id: 'drummer',
    gradeKo: '드러머',
    gradeEn: 'DRUMMER',
    titleKo: '드럼 전체 이용',
    titleEn: 'FULL DRUM ACCESS',
    priceKo: '8,900원',
    priceEn: '$7.99',
    rightsKo: ['모든 공개 곡', 'Easy·Normal·Hard 전체', '연주 기록'],
    rightsEn: ['All published songs', 'Easy, Normal and Hard', 'Performance records'],
    iconName: 'MusicNotes',
  },
  {
    id: 'cognitive',
    gradeKo: '인지',
    gradeEn: 'MIND',
    titleKo: '인지 전체 이용',
    titleEn: 'FULL MIND ACCESS',
    priceKo: '8,900원',
    priceEn: '$7.99',
    rightsKo: ['모든 인지 활동', '전체 단계 이용', '활동 기록'],
    rightsEn: ['All cognitive activities', 'Every activity stage', 'Activity records'],
    iconName: 'Brain',
  },
  {
    id: 'plus',
    gradeKo: '플러스',
    gradeEn: 'PLUS',
    titleKo: '드럼·인지 전체 이용',
    titleEn: 'DRUM & MIND ACCESS',
    priceKo: '14,900원',
    priceEn: '$12.99',
    rightsKo: ['드럼 전체 이용', '인지 전체 이용', '모든 활동 기록'],
    rightsEn: ['Full Drum access', 'Full Mind access', 'All activity records'],
    iconName: 'Crown',
  },
];

export const FORMAT_LABELS: Record<string, string> = {
  'color-drum': 'Color Drum',
  'color-staff': 'Color Staff',
  'classic-staff': 'Classic Staff',
};

export const DRUMS: Array<[string, string, string]> = [
  ['Crash', '#ef4455', 'A'],
  ['Tom 1', '#ff8a22', 'S'],
  ['Tom 2', '#ffd323', 'D'],
  ['Ride', '#35b85f', 'F'],
  ['Snare', '#3185ee', 'J'],
  ['Hi-hat', '#ef55a5', 'K'],
  ['Floor', '#8246d9', 'L'],
  ['Kick', '#8d97a3', 'Space'],
];

export const PREVIOUS_MISTAKES: Record<string, number> = { Crash: 4, 'Tom 1': 1, 'Tom 2': 2, Ride: 0, Snare: 1, 'Hi-hat': 1, Floor: 0, Kick: 2 };
export const SAMPLE_MISTAKES: Record<string, number> = { Crash: 2, 'Tom 1': 2, 'Tom 2': 1, Ride: 1, Snare: 1, 'Hi-hat': 0, Floor: 0, Kick: 1 };
export const KO_DRUM_NAMES: Record<string, string> = { Crash: '크래쉬 심벌', 'Tom 1': '탐 1', 'Tom 2': '탐 2', Ride: '라이드 심벌', Snare: '스네어', 'Hi-hat': '하이햇', Floor: '플로어탐', Kick: '킥' };
export const EN_DRUM_NAMES: Record<string, string> = { Crash: 'Crash Cymbal', Ride: 'Ride Cymbal' };

export const COACH_METRICS: Array<[string, string, string]> = [
  ['beatStability', '박자 안정성', 'Beat Stability'],
  ['timingControl', '타이밍 정확도', 'Timing Accuracy'],
  ['patternAccuracy', '패턴 재현', 'Pattern Accuracy'],
  ['tempoMaintenance', '템포 유지', 'Tempo Maintenance'],
  ['restRecovery', '쉼표 후 복귀', 'Rest Recovery'],
  ['limbCoordination', '손발 협응', 'Limb Coordination'],
];

export const JUDGMENT_SEQUENCE: string[] = ['Great', 'Great', 'Good', 'Great', 'Great', 'Bad', 'Great', 'Good', 'Great', 'Great', 'Great', 'Good', 'Bad', 'Great', 'Great', 'Good', 'Great', 'Great', 'Miss', 'Great', 'Good', 'Great', 'Great', 'Great'];

export interface PlayRecord {
  playId: string;
  scoreId?: string;
  title: string;
  difficulty: string;
  score: number;
  rank: string;
  accuracy: number;
  maxCombo: number;
  playedAt: string;
  durationMs?: number;
  stats?: { Great: number; Good: number; Bad: number; Miss: number };
  mistakes?: Record<string, number>;
  previousScore?: number;
}

export const INITIAL_RECORDS: PlayRecord[] = [];

export const KO: Record<string, string> = {
  'PLAY. GROW. ENJOY THE RHYTHM.': '리듬을 즐기며 몸과 생각을 함께 깨워보세요.',
  'Rhythm that moves you.': '연주와 인지활동을 한곳에서.',
  'Enjoy drum play and focused cognitive activities at a comfortable pace.': '편안한 속도로 드럼 연주와 집중력 중심의 인지활동을 함께 즐겨보세요.',
  'Continue with Google': 'Google로 계속하기',
  'By continuing, you agree to the Terms of Service and Privacy Policy.': '계속하면 서비스 이용약관과 개인정보 처리방침에 동의하게 됩니다.',
  Drum: '드럼', Mind: '인지', Records: '기록', My: 'MY',
  'Rhythm Pass': '드럼하이 패스', 'Mind Games': '인지 활동', 'Cognitive Activities': '인지 활동', 'My Profile': '내 정보', Results: '결과',
  'DAILY STAGE': '오늘의 스테이지', 'Feel the beat.': '리듬을 느끼고', 'Own the stage.': '무대를 완성하세요.',
  'One short session is ready. Start with a rhythm you already know.': '익숙한 리듬으로 짧은 연주를 시작해보세요.',
  'Play Midnight Drive': 'Midnight Drive 연주', 'day streak': '일 연속', 'QUICK PICK': '빠른 선택', 'Recommended for you': '추천 곡',
  'View all': '전체 보기', 'CONTINUE TRAINING': '이어서 학습', 'Timing Control · Session 3': '타이밍 조절 · 세션 3',
  'Two minutes to strengthen your steady beat.': '2분 동안 안정적인 박자를 훈련합니다.',
  Featured: '추천', Electronic: '일렉트로닉', Kids: '키즈', Pop: '팝', World: '월드', Rock: '록', Jazz: '재즈', Latin: '라틴',
  'Search songs or artists': '곡명 또는 아티스트 검색', 'No songs found.': '검색된 곡이 없습니다.', 'PREVIEW PLAYING': '미리듣기 재생 중',
  Easy: '이지', Normal: '노멀', Hard: '하드', 'Color Drum': '컬러드럼악보', 'Color Staff': '컬러오선악보', 'Classic Staff': '오선악보',
  'Colored circles, rests and measure bars': '색상 원형, 빈 원형 쉼표와 마디선', 'Full staff notation with instrument colors': '악기 색상이 적용된 오선악보',
  'Standard monochrome drum notation': '일반 흑백 드럼 오선악보', 'KEEP THE BEAT': '박자를 유지하세요', Paused: '일시정지',
  Resume: '계속하기', 'Leave Stage': '연주 종료', SCORE: '점수', COMBO: '콤보', 'STAGE COMPLETE': '스테이지 완료', 'FINAL SCORE': '최종 점수',
  'Personal best +3,240': '최고 기록 +3,240', 'Max Combo': '최대 콤보', Accuracy: '정확도',
  'COACH REVIEW': '코치 평가', 'Strong timing, steady progress.': '안정적인 타이밍으로 성장하고 있어요.', 'Beat Stability': '박자 안정성', Excellent: '매우 좋음',
  'Timing Control': '타이밍 조절', 'Pattern Recall': '패턴 기억', Growing: '성장 중',
  'You held a steady pulse through repeated snare patterns. On the next run, focus on the beat immediately after each rest to improve recovery timing.': '반복되는 스네어 패턴에서 안정적인 박자를 유지했습니다. 다음 연주에서는 쉼표 직후의 박자에 집중하면 복귀 타이밍을 더 정확하게 만들 수 있습니다.',
  'Play Again': '다시 연주', 'MIND TRAINING': '인지 훈련', 'Train attention through rhythm.': '리듬으로 집중력을 훈련하세요.',
  'Short game sessions blend listening, memory and reaction practice.': '짧은 게임으로 듣기, 기억력, 반응속도를 함께 훈련합니다.',
  'Memory Pulse': '리듬 기억', 'Remember and repeat a short color rhythm.': '짧은 색상 리듬을 기억하고 따라합니다.', Memory: '기억력',
  'Focus Switch': '집중 전환', 'Follow changing rules without losing the beat.': '박자를 유지하며 바뀌는 규칙을 따라갑니다.', Focus: '집중력',
  'Quick Response': '빠른 반응', 'React to the target sound at the right moment.': '목표 소리에 정확한 순간 반응합니다.', Reaction: '반응속도', Start: '시작',
  'Back to Mind Games': '인지 게임으로 돌아가기', 'MEMORY PULSE · STAGE 2': '리듬 기억 · 스테이지 2', 'Repeat the four-beat pattern.': '네 박자 패턴을 따라해보세요.',
  'Watch once, then tap the pads in the same order.': '한 번 본 뒤 같은 순서로 패드를 누르세요.', 'Pattern 2 of 5': '5개 중 2번째 패턴',
  'YOUR PROGRESS': '나의 성장', 'Performance Records': '연주 기록', 'Review play results and see which rhythm skills are improving.': '연주 결과와 리듬 능력의 변화를 확인합니다.',
  'Total Plays': '전체 연주', 'Average Accuracy': '평균 정확도', 'Best Combo': '최고 콤보', 'Practice Time': '연습 시간', Song: '곡', Level: '난이도', Score: '점수', Rank: '등급', Date: '날짜',
  'Unlock your full rhythm journey.': '모든 리듬 여정을 열어보세요.', 'Choose the plan that fits how you want to play and learn.': '연주와 학습 방식에 맞는 이용권을 선택하세요.',
  'Drum Pass': '드럼하이 패스', 'All public songs and every difficulty': '모든 공개 곡과 전체 난이도 이용', 'Full song library': '전체 곡 라이브러리', 'Easy, Normal and Hard': '이지, 노멀, 하드', 'Performance records': '연주 기록',
  'Mind Pass': '인지 패스', 'All rhythm-based cognitive sessions': '모든 리듬 기반 인지 세션', 'All Mind Games': '전체 인지 게임', 'Six learning stages': '6단계 학습', 'Progress reviews': '성장 평가',
  'All Access': '올 액세스', 'The complete DRUM Hi experience': 'DRUM Hi 전체 서비스 이용', 'All Drum Pass benefits': '드럼하이 패스 전체 혜택', 'All Mind Pass benefits': '인지 패스 전체 혜택', 'Best value': '최고 혜택', 'MOST POPULAR': '가장 인기',
  'Start All Access': '올 액세스 시작', 'Subscriptions renew automatically. Cancel anytime in Google Play.': '구독은 자동 갱신되며 Google Play에서 언제든 해지할 수 있습니다.', Back: '뒤로', 'PLAYER PROFILE': '사용자 정보', 'Joined July 14, 2026': '2026년 7월 14일 가입',
  'View Rhythm Pass': '드럼하이 패스 보기', Account: '계정', 'Profile details': '프로필 정보', Notifications: '알림', 'App settings': '앱 설정', Support: '고객지원', Notices: '공지사항', 'Help center': '도움말', 'Sign out': '로그아웃',
  ACCOUNT: '계정', SUPPORT: '고객지원', 'Review the account information used across DRUM Hi.': 'DRUM Hi에서 사용하는 계정 정보를 확인하고 수정합니다.',
  'Display name': '표시 이름', 'Google account': 'Google 이메일', Joined: '가입일', 'July 14, 2026': '2026년 7월 14일', 'Save changes': '변경사항 저장', Saved: '저장 완료',
  'Choose which DRUM Hi updates appear on this tablet.': '이 태블릿에서 받을 DRUM Hi 알림을 선택합니다.', 'New songs': '신곡 알림', 'Get notified when new playable songs are released.': '새로 연주할 수 있는 곡이 공개되면 알려드립니다.',
  'Notice updates': '공지사항', 'Get notified when a new service notice is published.': '새로운 서비스 공지사항이 등록되면 알려드립니다.',
  'New cognitive activities': '신규 인지 활동', 'Get notified when a new cognitive activity is available.': '새로운 인지 활동이 등록되면 알려드립니다.',
  NEW: '신규', 'Four new Global Pop songs are live.': '글로벌 팝 신곡 4곡이 공개되었습니다.', 'Explore fresh tracks in the song library.': '곡 라이브러리에서 새로운 음악을 만나보세요.', 'Read notice': '공지 보기',
  'Read the latest song, service and learning updates.': '곡, 서비스와 학습 관련 최신 소식을 확인하세요.', 'NEW SONGS': '신곡', SERVICE: '서비스', LEARNING: '학습',
  'Explore fresh tracks in Drum and try each score level.': '드럼에서 새로운 곡과 난이도별 악보를 확인해보세요.', 'DRUM Hi tablet preview update': '태블릿 미리듣기 업데이트',
  'Song previews now start automatically when a track is selected.': '곡을 선택하면 미리듣기가 자동으로 시작됩니다.', 'New rhythm coaching feedback': '새로운 리듬 코칭 평가',
  'Stage results now include timing, stability and pattern recall feedback.': '연주 결과에서 타이밍, 안정성, 패턴 기억 평가를 확인할 수 있습니다.',
  'Four new Global Pop tracks have been added to DRUM Hi.': 'DRUM Hi에 글로벌 팝 신곡 4곡이 추가되었습니다.', 'Open Drum, choose Pop, and select a song to hear its preview before playing. Easy, Normal, and Hard use their registered score formats.': '드럼에서 팝 카테고리를 선택한 뒤 곡을 누르면 연주 전에 미리듣기를 들을 수 있습니다. 이지, 노멀, 하드는 등록된 악보 형식으로 제공됩니다.',
  'Song preview playback now starts automatically when you select a track in Drum.': '드럼에서 곡을 선택하면 음원 미리듣기가 자동으로 시작됩니다.', 'You can change automatic preview playback from My > App settings at any time.': 'MY > 앱 설정에서 언제든 자동 미리듣기 설정을 변경할 수 있습니다.',
  'The result screen now combines game scores with learning feedback.': '결과 화면에서 게임 점수와 학습 평가를 함께 확인할 수 있습니다.', 'After each performance, review beat stability, timing control, pattern recall, accuracy, and maximum combo to plan the next practice session.': '연주 후 박자 안정성, 타이밍 조절, 패턴 기억, 정확도와 최대 콤보를 확인해 다음 연습을 계획하세요.',
  'NOTICE': '공지',
  'No notices yet.': '등록된 공지사항이 없습니다.',
  'Unable to load notices.': '공지사항을 불러오지 못했습니다.',
  'Unable to save changes.': '변경사항을 저장하지 못했습니다.',
  'Enter a display name.': '표시 이름을 입력해 주세요.',
  'Please sign in again.': '다시 로그인해 주세요.',
  'Loading...': '불러오는 중...',
  'Saving...': '저장 중...',
  'Notice details': '공지 상세', 'Back to Notices': '공지사항으로 돌아가기', Published: '등록일',
  Settings: '설정', Language: '언어', English: 'English', Korean: '한국어', Stage: '스테이지', songs: '곡', stages: '단계', 'Display language': '표시 언어', 'Choose the language used for menus, controls and learning feedback.': '메뉴, 조작 버튼과 학습 평가에 사용할 언어를 선택합니다.', 'Manage language, sound and play preferences.': '언어, 사운드와 연주 환경을 설정합니다.',
  'Song titles and artist names stay in their original language.': '곡명과 아티스트명은 원문으로 유지됩니다.', 'Sound & Play': '사운드 및 연주', 'Auto-play song preview': '곡 미리듣기 자동 재생', 'Judgment sound effects': '판정 효과음', 'Back to Profile': '내 정보로 돌아가기',
  'Find quick answers about songs, scores and account settings.': '곡, 점수와 계정 설정에 관한 도움말을 확인합니다.',
  'How do I choose a score level?': '악보 난이도는 어떻게 선택하나요?', 'Easy uses Color Drum circles, Normal uses a colored staff, and Hard uses classic monochrome drum notation.': '이지는 컬러드럼 원형 악보, 노멀은 컬러오선악보, 하드는 일반 흑백 오선악보를 사용합니다.',
  'Why does music start when I select a song?': '곡을 선택하면 왜 음악이 바로 재생되나요?', 'Automatic preview is enabled by default so you can hear each track before starting. You can change this in App settings.': '연주 전에 곡을 확인할 수 있도록 자동 미리듣기가 기본으로 켜져 있습니다. 앱 설정에서 변경할 수 있습니다.',
  'Where can I see my scores?': '내 점수는 어디서 확인하나요?', 'Open Records from the bottom menu to review score, rank, accuracy and play date.': '하단 메뉴의 기록에서 점수, 등급, 정확도와 연주일을 확인할 수 있습니다.',
  'How do I change the display language?': '화면 언어는 어떻게 변경하나요?', 'Open My, select App settings, then choose English or Korean.': 'MY에서 앱 설정을 열고 English 또는 한국어를 선택합니다.',
  Crash: '크래쉬 심벌', 'Tom 1': '탐 1', 'Tom 2': '탐 2', Ride: '라이드 심벌', Snare: '스네어', 'Hi-hat': '하이햇', Floor: '플로어탐',
};
